import "assets/scss/components/conversation/chat.scss";
import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Conversation, Custom, Form, Progress } from "components";
import { IconButton, Typography } from "@mui/material";
import { Chat as IChat } from "types";
import { IMessage, IUser } from "interfaces";
import { Constants, Helpers } from "utils";
import RefreshIcon from "@mui/icons-material/Refresh";

type Props = {
  chat: IChat | null;
};

export const Chat = (props: Props) => {
  const { user, service, t, theme, locale } = useApp();

  const [messages, setMessages] = useState<IMessage[]>([]);
  const refreshInterval = useRef<NodeJS.Timer | null>(null);
  const messagesEnd = useRef<any>(null);
  // TODO: using initialRender doesn't sound like an optimal solution
  const initialRender = useRef<boolean>(true);

  const receiver = props.chat?.receiver ?? ({} as IUser);
  const receiverUsername = Helpers.replaceComma(receiver.username ?? "");

  const isMine = (userId: number) => user.userId === userId;

  const handleSubmit = (message: IMessage) => {
    setMessages((prevMessages) => [...prevMessages, message]);
    scrollToBottom();
  };

  const scrollToBottom = () => {
    if (messagesEnd.current) {
      messagesEnd.current.scrollTop = messagesEnd.current.scrollHeight;
    }
  };

  const messagesEffect = async () => {
    const response = await service.message.chat(
      props.chat?.receiver.userId ?? -1
    );

    setMessages(response?.messages.reverse());
    scrollToBottom();
  };

  useEffect(() => {
    if (!initialRender.current) {
      if (props.chat) {
        messagesEffect();

        refreshInterval.current = setInterval(
          messagesEffect,
          Constants.REFRESH_RATE.CHAT
        );
      }
    } else {
      initialRender.current = false;
    }
    return () => {
      refreshInterval.current && clearInterval(refreshInterval.current);
    };
  }, [props.chat?.receiver.userId]);

  let currentDay: string = "";

  return (
    <div
      className="qh-conversation-chat"
      style={{ backgroundColor: theme.palette.background.accent }}
    >
      {props.chat ? (
        <>
          <div
            className="qh-header"
            style={{ color: theme.palette.font.color }}
          >
            <div className="qh-title">
              <Custom.Avatar name={receiverUsername} small />
              <Typography sx={{ marginLeft: theme.margin.md }} component="span">
                {receiverUsername}
              </Typography>
            </div>
            <div className="qh-control">
              <IconButton
                onClick={messagesEffect}
                sx={{ color: theme.palette.font.color }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </div>
          <div className="qh-content">
            <div className="qh-wrapper" ref={messagesEnd}>
              {messages.map((item: IMessage, i: number) => {
                const date: Date = new Date(item.messageTime);
                const day: string = Helpers.toRelativeDate(
                  item.messageTime,
                  t.relative_date,
                  locale,
                  true
                );
                const element: React.ReactNode = (
                  <Conversation.Message
                    key={i}
                    day={currentDay !== day ? day : null}
                    time={Helpers.toTimeAmPmString(
                      Helpers.toSeconds(date),
                      false
                    )}
                    mine={isMine(item.senderId)}
                  >
                    {item.content}
                  </Conversation.Message>
                );
                currentDay = day;
                return element;
              })}
            </div>
          </div>
          <div className="qh-footer">
            <Form.Chat
              receiverId={receiver.userId ?? -1}
              onSubmit={handleSubmit}
            />
          </div>
        </>
      ) : (
        <Progress.NoRecord
          height={250}
          message={t.message.placeholder.no_chat}
        />
      )}
    </div>
  );
};
